import { observer } from 'mobx-react-lite'
import Modal from '@ui-components-3/ui/lib/components/Modal'

import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CrisisEscalationSchema } from 'schemas/CrisisEscalation'
import User from 'singletons/User'
import { ErrorMessage } from '@hookform/error-message'
import ErrorFieldForm from 'components/UI/ErrorFieldForm'
import Select from '@ui-components-3/ui/lib/components/Select'
import Input from '@ui-components-3/ui/lib/components/Input'
import MaskInput from '@ui-components-3/ui/lib/components/MaskInput'
import Notice from '@ui-components-3/ui/lib/components/Notice'
import { useRef, useState } from 'react'
import PendingVisit from 'models/Provider/visits/PendingVisit'
import QueryWaiter from 'components/common/QueryWaiter'
import FormControl from '@ui-components-3/ui/lib/components/FormControl'

type CrisisEscalationProps = {
  visit: PendingVisit
  opened: boolean
  onClose: () => void
}

const tooltip = (
  <>
    <p>
      <b>Emergent:</b> A situation where, after assessment and de-escalation attempts within the scope of the
      responder's role, <b>the member is determined to be in immediate danger to themselves or others.</b> This
      necessitates immediate intervention from a first responder (e.g., 911, EMT, campus safety) and/or referral to the
      emergency room for further evaluation.
    </p>
    <p className={'pt-4'}>
      <b>Urgent:</b> A situation where the member reports a significant but not imminent risk to their well-being. While
      not an immediate life-threatening emergency, the situation requires prompt guidance and support from the Crisis
      Response Team to determine the appropriate next steps.
    </p>
  </>
)

const CrisisEscalation = ({ visit, opened, onClose }: CrisisEscalationProps) => {
  const [isEscalated, setIsEscalated] = useState(false)
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty, isValidating },
  } = useForm({
    resolver: yupResolver(CrisisEscalationSchema),
    mode: 'onSubmit',
    defaultValues: {
      reason: '',
      reasonOther: undefined,
      phoneNumber: User.provider?.preferredPhone?.number,
      notes: '',
    },
  })

  const formRef = useRef<HTMLFormElement>()

  const submitHandler = async (values) => {
    await visit?.crisisEscalation?.addEscalation?.(values)
    setIsEscalated(true)
  }

  return (
    <Modal
      label={'Crisis Escalation'}
      opened={opened}
      onClose={onClose}
      size="l"
      footer={() =>
        isEscalated ? (
          <>
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-neutral-outlined" onClick={onClose}>
              Cancel
            </button>
            <button
              disabled={isValidating || isSubmitting || !isDirty}
              type="button"
              className="btn btn-primary"
              onClick={() => formRef.current?.requestSubmit?.()}
            >
              <QueryWaiter query={['postVisitEscalation']} />
              Submit
            </button>
          </>
        )
      }
    >
      {isEscalated ? (
        <div className={'grid gap-s'}>
          <Notice variant={'info'}>
            <p className={'text-primary font-semibold'}>Escalation submitted successfully</p>
          </Notice>
          <p className={'px-4'}>Someone from the Crisis Support Team will be in touch within the next two minutes.</p>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit(submitHandler)} className="flex flex-col gap-m">
          <Controller
            name={'reason'}
            control={control}
            render={({ field }) => (
              <FormControl
                label={'Reason for Escalation*'}
                // @ts-expect-error FormControl prop types should be changed to allow ReactNode
                labelTooltipContent={tooltip}
                helper={errors.reason && <ErrorMessage errors={errors} name={'reason'} as={<ErrorFieldForm />} />}
              >
                <Select
                  {...field}
                  options={['Emergent', 'Urgent']}
                  getOptionValue={(opt) => opt.toLowerCase()}
                  getOptionLabel={(opt) => opt}
                />
              </FormControl>
            )}
          />
          <Controller
            name={'phoneNumber'}
            control={control}
            defaultValue=""
            render={({ field }) => {
              return (
                <FormControl
                  label={'Provider/Coach Phone Number*'}
                  helper={
                    errors.phoneNumber && <ErrorMessage errors={errors} name={'phoneNumber'} as={<ErrorFieldForm />} />
                  }
                >
                  <MaskInput
                    autoComplete="tel-national"
                    transform
                    mask="(___) ___-____"
                    placeholder="(000) 000-0000"
                    {...field}
                  />
                </FormControl>
              )
            }}
          />
          <FormControl label={'Describe the emergent/urgent situation'}>
            <Input
              label={'Describe the emergent/urgent situation'}
              placeholder={'Notes'}
              rows={4}
              autoComplete={'off'}
              multiline
              spellCheck="true"
              {...register('notes')}
            />
          </FormControl>
          <ErrorMessage errors={errors} name={'notes'} as={<ErrorFieldForm />} />
          <Notice variant="error">
            <p className={'typography-small text-sm'}>
              Please submit a referral for routine follow-up requests or urgent situations that do not require immediate
              escalation to CRT. If no immediate guidance or support from CRT is needed, do not proceed with crisis
              escalation.
            </p>
          </Notice>
        </form>
      )}
    </Modal>
  )
}

export default observer(CrisisEscalation)
